import React, { useState, useEffect } from 'react';
import {
    GraduationCap,
    Calendar,
    DollarSign,
    Users,
    BookOpen,
    FileText,
    Award,
    Target,
    CheckCircle,
    Mail,
    ArrowRight,
    Loader2,
    Clock,
    CreditCard,
    Building2,
    TrendingUp,
    Globe,
    Microscope,
    Beaker,
    FlaskConical,
    Network,
    Briefcase,
    Lightbulb,
    User,
    Send
} from 'lucide-react';
import { useFormSubmit } from '../hooks/useFormSubmit';

const SeminarioSNII = () => {
  const [data, setData] = useState(null);
  const [datosBancarios, setDatosBancarios] = useState(null);
  const [isLoading, setIsLoading] = useState(true);
  const [error, setError] = useState(null);
  const [formData, setFormData] = useState({
    nombre: '',
    email: '',
    institucion: '',
    actividades: '',
    mensaje: '',
    acepto: false
  });

  // Hook para envío de formulario
  const { loading: submitting, success: submitSuccess, error: submitError, submitForm, resetForm } = useFormSubmit('seminario-snii');

  useEffect(() => {
        const loadData = async () => {
            try {
                setIsLoading(true);
                const [seminarioRes, generalesRes] = await Promise.all([
                    fetch(`/data/seminario-snii.json?v=${Date.now()}`),
                    fetch(`/data/generales.json?v=${Date.now()}`)
                ]);

                if (!seminarioRes.ok) throw new Error('Error al cargar los datos del seminario');
                if (!generalesRes.ok) throw new Error('Error al cargar los datos generales');

                const seminarioData = await seminarioRes.json();
                const generalesData = await generalesRes.json();

                setData(seminarioData);
                setDatosBancarios(generalesData.datosBancarios);
                setError(null);
            } catch (err) {
                console.error('Error cargando datos:', err);
                setError(err.message);
            } finally {
                setIsLoading(false);
            }
        };
        loadData();
    }, []);

    const handleInputChange = (e) => {
        const { name, value, type, checked } = e.target;
        setFormData(prev => ({
            ...prev,
            [name]: type === 'checkbox' ? checked : value
        }));
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        
        // Preparar datos para enviar
        const dataToSend = {
            ...formData,
            correo_destino: data.contacto, // seminariosnii@cenid.org
            asunto: 'Nueva solicitud de información - Seminario SNII',
            programa: 'Seminario de Alta Especialización para la Evaluación SNII'
        };

        const result = await submitForm(dataToSend);
        
        if (result.success) {
            // Limpiar formulario
            setFormData({
                nombre: '',
                email: '',
                institucion: '',
                actividades: '',
                mensaje: '',
                acepto: false
            });
        }
    };

    if (isLoading) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <Loader2 className="w-16 h-16 text-[#0b742f] animate-spin mx-auto mb-4" />
                    <p className="text-gray-600 text-lg">Cargando información del seminario...</p>
                </div>
            </div>
        );
    }

    if (error || !data) {
        return (
            <div className="min-h-screen flex items-center justify-center">
                <div className="text-center">
                    <p className="text-red-600 text-lg mb-4">Error: {error || 'No se pudieron cargar los datos'}</p>
                    <button
                        onClick={() => window.location.reload()}
                        className="bg-[#0b742f] text-white px-6 py-2 rounded-lg hover:bg-[#085a24]"
                    >
                        Reintentar
                    </button>
                </div>
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-gradient-to-b from-gray-50 to-white">
            {/* Hero Section */}
            <div className="bg-gradient-to-br from-[#0b742f] via-[#085a24] to-[#064519] text-white">
                <div className="max-w-7xl mx-auto px-6 py-20">
                    <div>
                        <div className="inline-flex items-center gap-2 bg-white/20 backdrop-blur-sm px-4 py-2 rounded-full mb-6">
                            <Microscope className="w-5 h-5" />
                            <span className="text-sm font-semibold">Programa de Formación en Evaluación Científica</span>
                        </div>
                        <h1 className="text-5xl md:text-6xl font-bold mb-6">
                            {data.titulo}
                        </h1>
                        <p className="text-xl text-green-100 max-w-3xl mb-6">
                            {data.subtitulo}
                        </p>
                    </div>
                </div>
            </div>

            <div className="max-w-7xl mx-auto px-6 py-16">
                {/* Quick Info Cards */}
                <div className="grid md:grid-cols-2 gap-8 -mt-12 mb-16 relative z-10 max-w-4xl mx-auto">
                    <div className="bg-white rounded-xl shadow-xl p-8 border-t-4 border-[#0b742f] hover:scale-105 transition-transform duration-300">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="bg-[#0b742f]/10 p-3 rounded-lg">
                                <Calendar className="w-7 h-7 text-[#0b742f]" />
                            </div>
                            <h3 className="font-bold text-gray-900 text-base">Inscripciones</h3>
                        </div>
                        <p className="text-xl font-bold text-[#0b742f]">{data.inscripciones.periodo}</p>
                    </div>

                    <div className="bg-white rounded-xl shadow-xl p-8 border-t-4 border-[#e87729] hover:scale-105 transition-transform duration-300">
                        <div className="flex items-center gap-3 mb-4">
                            <div className="bg-[#e87729]/10 p-3 rounded-lg">
                                <Globe className="w-7 h-7 text-[#e87729]" />
                            </div>
                            <h3 className="font-bold text-gray-900 text-base">Modalidad</h3>
                        </div>
                        <p className="text-xl font-bold text-[#e87729]">{data.modalidad}</p>
                    </div>
                </div>

                {/* Convocatoria Completa */}
                <section className="mb-16">
                    <div className="bg-gradient-to-br from-white to-gray-50 rounded-2xl shadow-xl p-8 md:p-12 border-l-4 border-[#0b742f]">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="bg-[#0b742f] p-3 rounded-lg">
                                <BookOpen className="w-8 h-8 text-white" />
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Convocatoria</h2>
                        </div>

                        <div className="space-y-6">
                            <p className="text-gray-700 text-lg leading-relaxed">
                                El Centro de Estudios e Investigaciones para el Desarrollo Docente CENID A.C. con el fin de apoyar el desarrollo de proyectos de investigación científica, humanística y tecnológica que generen conocimiento de frontera, y que contribuyan a la consolidación de la trayectoria académica así como el ofrecer estrategias de liderazgo y prestigio en la investigación, a la formación de científicos y al bienestar de la sociedad.
                            </p>

                            <div className="text-center py-6">
                                <h3 className="text-3xl font-bold text-[#0b742f] tracking-wide">CONVOCA</h3>
                                <div className="h-1 w-32 bg-gradient-to-r from-transparent via-[#0b742f] to-transparent rounded mx-auto mt-3"></div>
                            </div>

                            <p className="text-gray-700 text-lg leading-relaxed">
                                A las profesoras y los profesores de Instituciones de Educación Superior a participar en el seminario de alta especialización para la evaluación SNII cuyo objetivo es que las y los investigadores logren alcanzar parámetros de referencia en el perfil de evaluación que se ajuste a su trayectoria: investigación científica y humanística, incidencia socioambiental, o desarrollo tecnológico e innovación. Cada perfil tiene criterios de evaluación específicos, que consideran la producción científica, el acceso universal al conocimiento y la formación de comunidades.
                            </p>
                        </div>
                    </div>
                </section>

                {/* Objetivo */}
                <section className="mb-16">
                    <div className="bg-gradient-to-br from-[#e87729] to-[#d66820] text-white rounded-2xl shadow-xl p-8 md:p-12">
                        <div className="flex items-center gap-3 mb-6">
                            <Target className="w-8 h-8" />
                            <h2 className="text-3xl font-bold">Objetivo del Seminario</h2>
                        </div>
                        <p className="text-lg leading-relaxed text-orange-50 mb-6">
                            Apoyar a los investigadores en el logro de parámetros de referencia en el perfil de evaluación SNII que se ajuste a su trayectoria profesional y científica.
                        </p>
                        <div className="grid md:grid-cols-3 gap-4">
                            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-5 hover:bg-white/30 transition-all duration-300">
                                <div className="flex items-start gap-3">
                                    <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1" />
                                    <span className="font-semibold">Investigación científica y humanística</span>
                                </div>
                            </div>
                            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-5 hover:bg-white/30 transition-all duration-300">
                                <div className="flex items-start gap-3">
                                    <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1" />
                                    <span className="font-semibold">Incidencia socioambiental</span>
                                </div>
                            </div>
                            <div className="bg-white/20 backdrop-blur-sm rounded-xl p-5 hover:bg-white/30 transition-all duration-300">
                                <div className="flex items-start gap-3">
                                    <CheckCircle className="w-6 h-6 flex-shrink-0 mt-1" />
                                    <span className="font-semibold">Desarrollo tecnológico e innovación</span>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Perfiles de Evaluación */}
                <section className="mb-16">
                    <div className="bg-gradient-to-br from-indigo-50 to-purple-50 rounded-2xl shadow-xl p-8 md:p-12 border-l-4 border-indigo-600">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="bg-indigo-600 p-3 rounded-lg">
                                <Beaker className="w-8 h-8 text-white" />
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Perfiles de Evaluación SNII</h2>
                        </div>
                        <p className="text-gray-700 text-lg mb-6 leading-relaxed">
                            Cada perfil tiene criterios de evaluación específicos, que consideran la producción científica, el acceso universal al conocimiento y la formación de comunidades.
                        </p>
                        <div className="grid md:grid-cols-3 gap-6">
                            {data.perfilesEvaluacion.map((perfil, index) => (
                                <div key={index} className="bg-white rounded-xl shadow-lg p-6 border-2 border-indigo-100 hover:border-indigo-400 hover:shadow-xl transition-all duration-300 group">
                                    <div className="flex items-start gap-3 mb-4">
                                        <div className="bg-indigo-100 group-hover:bg-indigo-600 p-3 rounded-lg transition-colors duration-300">
                                            <FlaskConical className="w-6 h-6 text-indigo-600 group-hover:text-white" />
                                        </div>
                                        <h3 className="text-xl font-bold text-gray-900 flex-1">{perfil.perfil}</h3>
                                    </div>
                                    <p className="text-gray-700 leading-relaxed">{perfil.descripcion}</p>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Destinatarios */}
                <section className="mb-16">
                    <div className="bg-gradient-to-br from-blue-50 to-indigo-50 rounded-2xl shadow-xl p-8 md:p-12 border-l-4 border-blue-600">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="bg-blue-600 p-3 rounded-lg">
                                <Users className="w-8 h-8 text-white" />
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Destinado a</h2>
                        </div>
                        <div className="grid md:grid-cols-2 gap-4">
                            {data.destinatarios.map((dest, index) => (
                                <div key={index} className="bg-white rounded-lg shadow-md hover:shadow-lg transition-all duration-300 p-5 border border-blue-100 hover:border-blue-400 group">
                                    <div className="flex items-center gap-3">
                                        <div className="bg-blue-100 group-hover:bg-blue-600 p-2 rounded-lg transition-colors duration-300">
                                            <CheckCircle className="w-6 h-6 text-blue-600 group-hover:text-white" />
                                        </div>
                                        <span className="text-gray-900 font-semibold">{dest}</span>
                                    </div>
                                </div>
                            ))}
                        </div>
                    </div>
                </section>

                {/* Actividades de Vinculación */}
                <section id="actividades" className="mb-16">
                    <div className="bg-white rounded-2xl shadow-xl p-8 md:p-12">
                        <div className="text-center mb-12">
                            <div className="inline-flex items-center gap-3 mb-4">
                                <div className="bg-[#0b742f] p-3 rounded-lg">
                                    <Network className="w-8 h-8 text-white" />
                                </div>
                                <h2 className="text-4xl font-bold text-gray-900">Actividades de Vinculación CENID</h2>
                            </div>
                            <div className="h-1 w-32 bg-gradient-to-r from-[#0b742f] to-[#e87729] rounded mx-auto mb-6"></div>
                            <p className="text-gray-700 text-lg max-w-4xl mx-auto">
                                Las actividades en las que el CENID ofrece vinculación a las y los investigadores para alcanzar parámetros de referencia en el perfil de evaluación SNII
                            </p>
                        </div>

                        <div className="overflow-x-auto">
                            <table className="w-full border-collapse">
                                <thead>
                                    <tr className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white">
                                        <th className="border border-gray-300 px-6 py-4 text-left font-bold">Parámetro</th>
                                        <th className="border border-gray-300 px-6 py-4 text-left font-bold">Actividad propuesta</th>
                                        <th className="border border-gray-300 px-6 py-4 text-left font-bold">Descripción de la actividad</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    {data.actividadesVinculacion.map((item, index) => (
                                        <tr key={index} className={`${index % 2 === 0 ? 'bg-gray-50' : 'bg-white'} hover:bg-green-50 transition-colors duration-200`}>
                                            <td className="border border-gray-300 px-6 py-4 font-semibold text-gray-900">
                                                <div className="flex items-start gap-2">
                                                    <Briefcase className="w-5 h-5 text-[#0b742f] flex-shrink-0 mt-0.5" />
                                                    {item.parametro}
                                                </div>
                                            </td>
                                            <td className="border border-gray-300 px-6 py-4 text-gray-800 font-medium">
                                                {item.actividad}
                                            </td>
                                            <td className="border border-gray-300 px-6 py-4 text-gray-700">
                                                {item.descripcion}
                                            </td>
                                        </tr>
                                    ))}
                                </tbody>
                            </table>
                        </div>

                        {/* Info adicional */}
                        <div className="mt-8 bg-gradient-to-r from-yellow-50 to-orange-50 border-l-4 border-yellow-500 rounded-r-xl p-6">
                            <div className="flex items-start gap-3">
                                <Lightbulb className="w-6 h-6 text-yellow-600 flex-shrink-0 mt-1" />
                                <div>
                                    <h3 className="font-bold text-gray-900 mb-2 text-lg">Nota Importante</h3>
                                    <p className="text-gray-700 leading-relaxed">
                                        {data.notaImportante}
                                    </p>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>

                {/* Información de Contacto */}
                <section className="mb-16">
                    <div className="bg-gradient-to-br from-gray-50 to-white rounded-2xl shadow-xl p-8 md:p-12">
                        <div className="flex items-center gap-3 mb-8">
                            <div className="bg-green-600 p-3 rounded-lg">
                                <CreditCard className="w-8 h-8 text-white" />
                            </div>
                            <h2 className="text-3xl font-bold text-gray-900">Información e Inscripciones</h2>
                        </div>

                        {/* Franja Verde con Costo */}
                        <div className="bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white rounded-lg p-6 mb-8">
                            <div className="flex flex-col md:flex-row items-center justify-between gap-4">
                                <div>
                                    <h3 className="text-2xl font-bold mb-2">Inscripción por participante</h3>
                                    <p className="text-white/90">{data.costoDescripcion}</p>
                                </div>
                                <div className="text-center">
                                    <div className="text-5xl font-bold">${data.costo}</div>
                                    <p className="text-white/90 text-sm mt-1">MXN</p>
                                </div>
                            </div>
                        </div>

                        <div className="grid md:grid-cols-2 gap-8 mb-8">
                            {/* Contacto */}
                            <div className="bg-gradient-to-br from-green-600 to-emerald-700 text-white rounded-2xl shadow-lg p-8">
                                <div className="flex items-center gap-3 mb-6">
                                    <Mail className="w-10 h-10" />
                                    <div>
                                        <h3 className="text-2xl font-bold">Más Información</h3>
                                        <p className="text-green-100 text-sm">Contáctanos para resolver tus dudas</p>
                                    </div>
                                </div>
                                <div className="bg-white/20 backdrop-blur-sm rounded-xl p-6 mb-6">
                                    <p className="text-green-100 text-sm mb-2">Correo electrónico</p>
                                    <p className="text-xl font-bold break-all">{data.contacto}</p>
                                </div>
                                <div className="space-y-2">
                                    <div className="flex items-center gap-2 text-green-50">
                                        <CheckCircle className="w-5 h-5" />
                                        <span className="text-sm">Respuesta en 24-48 horas</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-green-50">
                                        <CheckCircle className="w-5 h-5" />
                                        <span className="text-sm">Asesoría personalizada</span>
                                    </div>
                                    <div className="flex items-center gap-2 text-green-50">
                                        <CheckCircle className="w-5 h-5" />
                                        <span className="text-sm">Material informativo incluido</span>
                                    </div>
                                </div>
                            </div>

                            {/* Sitio Web */}
                            <div className="bg-white border-2 border-green-200 rounded-2xl p-8 flex flex-col justify-center">
                                <h3 className="text-2xl font-bold text-gray-900 mb-6 flex items-center gap-2">
                                    <Globe className="w-7 h-7 text-green-600" />
                                    Visita nuestro sitio web
                                </h3>
                                <a
                                    href={data.sitioWeb}
                                    target="_blank"
                                    rel="noopener noreferrer"
                                    className="inline-flex items-center justify-center gap-3 bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white px-8 py-4 rounded-xl font-bold text-xl hover:shadow-xl transition-all duration-300 hover:scale-105"
                                >
                                    <Globe className="w-6 h-6" />
                                    {data.sitioWeb.replace('https://', '')}
                                    <ArrowRight className="w-6 h-6" />
                                </a>
                                <p className="text-gray-600 text-sm mt-4 text-center">
                                    Encuentra más información sobre nuestros programas y servicios
                                </p>
                            </div>
                        </div>

                        {/* Datos Bancarios (si aplica) 
            {datosBancarios && (
              <div className="bg-gradient-to-br from-gray-100 to-gray-200 rounded-2xl p-8 border-t-4 border-gray-700">
                <h4 className="font-bold text-gray-900 mb-6 text-2xl flex items-center gap-3">
                  <Building2 className="w-7 h-7 text-gray-700" />
                  Datos Bancarios
                </h4>
                <div className="grid md:grid-cols-2 gap-4">
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-sm text-gray-600 mb-1">Titular</p>
                    <p className="font-bold text-gray-900">{datosBancarios.titular}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-sm text-gray-600 mb-1">Banco</p>
                    <p className="font-bold text-gray-900">{datosBancarios.banco}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-sm text-gray-600 mb-1">Número de Cuenta</p>
                    <p className="font-bold text-gray-900 text-lg">{datosBancarios.numeroCuenta}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-sm text-gray-600 mb-1">CLABE Interbancaria</p>
                    <p className="font-bold text-gray-900 text-lg">{datosBancarios.clabe}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-sm text-gray-600 mb-1">SWIFT</p>
                    <p className="font-bold text-gray-900">{datosBancarios.swift}</p>
                  </div>
                  <div className="bg-white rounded-lg p-4 shadow-sm">
                    <p className="text-sm text-gray-600 mb-1">Sucursal</p>
                    <p className="font-bold text-gray-900 text-sm">{datosBancarios.sucursal}</p>
                  </div>
                </div>
              </div>
            )}
              */}
                    </div>
                </section>

                {/* Formulario de Solicitud */}
                <section className="mb-16">
                    <div className="bg-white rounded-lg shadow-xl overflow-hidden border-t-4 border-[#0b742f]">
                        <div className="p-8">
                            <h2 className="text-3xl font-bold text-gray-900 mb-4 flex items-center gap-3">
                                <FileText className="w-8 h-8 text-[#0b742f]" />
                                Formulario de solicitud de información
                            </h2>
                            <p className="text-gray-600 mb-6">Los campos marcados con * son obligatorios</p>

                            {/* Mensaje de éxito */}
                            {submitSuccess && (
                                <div className="bg-green-50 border-l-4 border-green-500 rounded-lg p-4 mb-6">
                                    <div className="flex items-center gap-3">
                                        <CheckCircle className="w-6 h-6 text-green-600 flex-shrink-0" />
                                        <div>
                                            <h3 className="font-bold text-green-900">¡Solicitud enviada exitosamente!</h3>
                                            <p className="text-green-700 text-sm">Nos pondremos en contacto contigo pronto a través del correo proporcionado.</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            {/* Mensaje de error */}
                            {submitError && (
                                <div className="bg-red-50 border-l-4 border-red-500 rounded-lg p-4 mb-6">
                                    <div className="flex items-center gap-3">
                                        <Mail className="w-6 h-6 text-red-600 flex-shrink-0" />
                                        <div>
                                            <h3 className="font-bold text-red-900">Error al enviar</h3>
                                            <p className="text-red-700 text-sm">{submitError}</p>
                                        </div>
                                    </div>
                                </div>
                            )}

                            <form onSubmit={handleSubmit} className="space-y-6">
                                {/* Nombre completo */}
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">
                                        Nombre completo <span className="text-red-600">*</span>
                                    </label>
                                    <div className="relative">
                                        <User className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                                        <input
                                            type="text"
                                            name="nombre"
                                            value={formData.nombre}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0b742f] focus:border-transparent"
                                            placeholder="Tu nombre completo"
                                        />
                                    </div>
                                </div>

                                {/* Email */}
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">
                                        Correo electrónico <span className="text-red-600">*</span>
                                    </label>
                                    <div className="relative">
                                        <Mail className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                                        <input
                                            type="email"
                                            name="email"
                                            value={formData.email}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0b742f] focus:border-transparent"
                                            placeholder="tu@email.com"
                                        />
                                    </div>
                                </div>

                                {/* Institución */}
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">
                                        Institución <span className="text-red-600">*</span>
                                    </label>
                                    <div className="relative">
                                        <Building2 className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                                        <input
                                            type="text"
                                            name="institucion"
                                            value={formData.institucion}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0b742f] focus:border-transparent"
                                            placeholder="Universidad o institución"
                                        />
                                    </div>
                                </div>

                                {/* Actividades de interés */}
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">
                                        Actividades de interés <span className="text-red-600">*</span>
                                    </label>
                                    <div className="relative">
                                        <Award className="absolute left-3 top-3 w-5 h-5 text-gray-400" />
                                        <input
                                            type="text"
                                            name="actividades"
                                            value={formData.actividades}
                                            onChange={handleInputChange}
                                            required
                                            className="w-full pl-10 pr-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0b742f] focus:border-transparent"
                                            placeholder="Ej: Congresos, publicaciones, talleres..."
                                        />
                                    </div>
                                </div>

                                {/* Mensaje adicional */}
                                <div>
                                    <label className="block text-gray-700 font-semibold mb-2">
                                        Mensaje adicional (opcional)
                                    </label>
                                    <textarea
                                        name="mensaje"
                                        value={formData.mensaje}
                                        onChange={handleInputChange}
                                        rows={4}
                                        className="w-full px-4 py-3 border border-gray-300 rounded-lg focus:ring-2 focus:ring-[#0b742f] focus:border-transparent resize-none"
                                        placeholder="Cuéntanos más sobre tu interés en el seminario..."
                                    />
                                </div>

                                {/* Checkbox de aceptación */}
                                <div className="flex items-start gap-3">
                                    <input
                                        type="checkbox"
                                        name="acepto"
                                        id="acepto"
                                        checked={formData.acepto}
                                        onChange={handleInputChange}
                                        required
                                        className="mt-1 w-5 h-5 text-[#0b742f] border-gray-300 rounded focus:ring-[#0b742f]"
                                    />
                                    <label htmlFor="acepto" className="text-sm text-gray-700">
                                        Acepto que mis datos sean utilizados para contactarme sobre el Seminario SNII. <span className="text-red-600">*</span>
                                    </label>
                                </div>

                                {/* Botón de envío */}
                                <button
                                    type="submit"
                                    disabled={submitting}
                                    className="w-full md:w-auto bg-gradient-to-r from-[#0b742f] to-[#085a24] text-white px-8 py-4 rounded-lg font-bold text-lg hover:shadow-xl transition-all duration-300 flex items-center justify-center gap-3 hover:scale-105 disabled:opacity-50 disabled:cursor-not-allowed disabled:hover:scale-100"
                                >
                                    {submitting ? (
                                        <>
                                            <Loader2 className="w-6 h-6 animate-spin" />
                                            Enviando...
                                        </>
                                    ) : (
                                        <>
                                            <Send className="w-6 h-6" />
                                            Enviar solicitud
                                        </>
                                    )}
                                </button>
                            </form>
                        </div>
                    </div>
                </section>

                {/* Contacto CTA */}
                <section>
                    <div className="bg-gradient-to-br from-[#0b742f] via-[#085a24] to-[#064519] rounded-2xl shadow-2xl overflow-hidden">
                        <div className="p-8 md:p-12 text-center relative">
                            <div className="absolute inset-0 bg-[url('data:image/svg+xml;base64,PHN2ZyB3aWR0aD0iNjAiIGhlaWdodD0iNjAiIHZpZXdCb3g9IjAgMCA2MCA2MCIgeG1sbnM9Imh0dHA6Ly93d3cudzMub3JnLzIwMDAvc3ZnIj48ZyBmaWxsPSJub25lIiBmaWxsLXJ1bGU9ImV2ZW5vZGQiPjxnIGZpbGw9IiNmZmYiIGZpbGwtb3BhY2l0eT0iMC4wNSI+PHBhdGggZD0iTTM2IDE0YzMuMzEgMCA2IDIuNjkgNiA2cy0yLjY5IDYtNiA2LTYtMi42OS02LTYgMi42OS02IDYtNnptMCAyYy0yLjIxIDAtNCAxLjc5LTQgNHMxLjc5IDQgNCA0IDQtMS43OSA0LTQtMS43OS00LTQtNHoiLz48L2c+PC9nPjwvc3ZnPg==')] opacity-50"></div>
                            <div className="relative z-10">
                                <div className="bg-white/10 backdrop-blur-sm rounded-full w-24 h-24 flex items-center justify-center mx-auto mb-6">
                                    <Mail className="w-12 h-12 text-white" />
                                </div>
                                <h2 className="text-4xl md:text-5xl font-bold text-white mb-4">
                                    ¿Listo para fortalecer tu perfil de investigador?
                                </h2>
                                <p className="text-xl text-green-100 mb-8 max-w-2xl mx-auto">
                                    Contáctanos para obtener más información sobre el Seminario SNII
                                </p>
                                <div className="flex flex-col sm:flex-row gap-4 justify-center items-center">
                                    <a
                                        href={`mailto:${data.contacto}`}
                                        className="inline-flex items-center gap-3 bg-white text-[#0b742f] px-10 py-5 rounded-xl font-bold text-xl hover:bg-gray-100 transition-all duration-300 shadow-2xl hover:scale-105 break-all"
                                    >
                                        <Mail className="w-7 h-7 flex-shrink-0" />
                                        <span className="break-all">{data.contacto}</span>
                                    </a>
                                    <a
                                        href={data.sitioWeb}
                                        target="_blank"
                                        rel="noopener noreferrer"
                                        className="inline-flex items-center gap-3 bg-[#e87729] text-white px-10 py-5 rounded-xl font-bold text-xl hover:bg-[#d96820] transition-all duration-300 shadow-2xl hover:scale-105"
                                    >
                                        <Globe className="w-7 h-7" />
                                        Más Información
                                    </a>
                                </div>
                            </div>
                        </div>
                    </div>
                </section>
            </div>
        </div>
    );
};

export default SeminarioSNII;
